import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import { ISkuQualificationRequest } from "services/KnowfullyAdminClient";
import type { ReferenceRow } from "./qualificationLinkState";
import {
  creditLinesSourceId,
  emptyWizardFormValues,
  toSkuQualificationRequests,
  type EditTarget,
  type WizardFormValues,
} from "./wizardSave";
import Step2ConfigureDetails from "./Step2ConfigureDetails";

// Edit is Accreditation-Body level: no step header, and the link selection stays as created.
// `disabledFields` are the fields a bulk edit must not overwrite; empty means all editable.
const styles = {
  titleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    pr: 1,
  },
};

type EditProps = {
  open: boolean;
  onClose: () => void;
  entries: ReferenceRow[];
  // What the save replaces: the edited row itself, or the members of the edited group.
  editTarget?: EditTarget;
  initialValues?: WizardFormValues;
  disabledFields?: Set<string>;
  skuCredits?: number;
  onSave: (
    requests: ISkuQualificationRequest[],
  ) => Promise<{ [key: string]: string } | undefined>;
};

export default function EditQualificationDialog({
  open,
  onClose,
  entries,
  editTarget,
  initialValues,
  disabledFields,
  skuCredits,
  onSave,
}: EditProps) {
  const { formatMessage: t } = useIntl();
  const creditsSourceId = creditLinesSourceId(editTarget);

  const handleSubmit = (values: WizardFormValues) =>
    onSave(
      toSkuQualificationRequests(entries, values, editTarget),
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={styles.titleBar}>
        <DialogTitle>{t({ id: "skuQualificationEditTitle" })}</DialogTitle>
        <IconButton onClick={onClose} aria-label={t({ id: "closeButton" })}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Form<WizardFormValues>
        onSubmit={handleSubmit}
        initialValues={initialValues ?? emptyWizardFormValues()}
        render={({ handleSubmit: submit, submitting, submitError, values }) => {
          const canSave = (values.professionIds?.length ?? 0) > 0;

          return (
            <form onSubmit={submit} noValidate>
              <DialogContent dividers>
                {submitError && <Alert severity="error">{submitError}</Alert>}

                <Step2ConfigureDetails
                  entries={entries}
                  disabledFields={disabledFields}
                  skuQualificationId={creditsSourceId}
                  creditsVary={!creditsSourceId && !!editTarget?.childSkuQualificationIds?.length}
                  skuCredits={skuCredits}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>{t({ id: "cancelButton" })}</Button>
                <Box sx={{ flex: 1 }} />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSave || submitting}
                  startIcon={submitting && <CircularProgress size={16} />}
                >
                  {t({ id: "saveButton" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
}
